export type ConsultationVoiceTranscriptionStatus = 'pending' | 'completed' | 'failed' | 'skipped';

export type ConsultationVoiceSchema = {
  enableTranscription?: boolean;
  maxDurationSeconds?: number;
  maxFileSizeBytes?: number;
};

export type ConsultationVoiceAudioItem = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  objectKey?: string;
  uploadedAtUtc?: string;
  durationSeconds?: number;
};

export type ConsultationVoiceTranscriptionItem = {
  text?: string;
  status: ConsultationVoiceTranscriptionStatus;
  updatedAtUtc?: string;
  error?: string;
};

export type ConsultationVoiceValue = {
  audio?: ConsultationVoiceAudioItem;
  transcription?: ConsultationVoiceTranscriptionItem;
};

export const DEFAULT_VOICE_SCHEMA: ConsultationVoiceSchema = {
  enableTranscription: true,
  maxDurationSeconds: 300,
  maxFileSizeBytes: 10 * 1024 * 1024,
};

export const DEFAULT_VOICE_SCHEMA_JSON = JSON.stringify(DEFAULT_VOICE_SCHEMA, null, 2);

const readJsonString = (source: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value !== undefined && value !== null) {
      return String(value).trim();
    }
  }

  return '';
};

const readJsonNumber = (source: Record<string, unknown>, ...keys: string[]): number => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return 0;
};

const normalizeAudioItem = (raw: unknown): ConsultationVoiceAudioItem | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const id = readJsonString(item, 'id', 'Id');
  const fileName = readJsonString(item, 'fileName', 'FileName');
  const contentType = readJsonString(item, 'contentType', 'ContentType');
  const url = readJsonString(item, 'url', 'Url');

  if (!id || !fileName || !contentType || !url) {
    return null;
  }

  const objectKey = readJsonString(item, 'objectKey', 'ObjectKey');
  const uploadedAtUtc = readJsonString(item, 'uploadedAtUtc', 'UploadedAtUtc');
  const durationSeconds = readJsonNumber(item, 'durationSeconds', 'DurationSeconds');

  return {
    id,
    fileName,
    contentType,
    sizeBytes: readJsonNumber(item, 'sizeBytes', 'SizeBytes'),
    url,
    ...(objectKey ? { objectKey } : {}),
    ...(uploadedAtUtc ? { uploadedAtUtc } : {}),
    ...(durationSeconds > 0 ? { durationSeconds } : {}),
  };
};

const normalizeTranscriptionItem = (raw: unknown): ConsultationVoiceTranscriptionItem | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const status = readJsonString(item, 'status', 'Status') as ConsultationVoiceTranscriptionStatus;
  if (!status) {
    return null;
  }

  const text = readJsonString(item, 'text', 'Text');
  const updatedAtUtc = readJsonString(item, 'updatedAtUtc', 'UpdatedAtUtc');
  const error = readJsonString(item, 'error', 'Error');

  return {
    status,
    ...(text ? { text } : {}),
    ...(updatedAtUtc ? { updatedAtUtc } : {}),
    ...(error ? { error } : {}),
  };
};

export const parseConsultationVoiceSchema = (schemaJson?: string): ConsultationVoiceSchema | null => {
  if (!schemaJson?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(schemaJson) as Record<string, unknown>;
    const maxDurationSeconds = (parsed.maxDurationSeconds ?? parsed.MaxDurationSeconds) as number | undefined;
    const maxFileSizeBytes = (parsed.maxFileSizeBytes ?? parsed.MaxFileSizeBytes) as number | undefined;
    if (!maxDurationSeconds || maxDurationSeconds <= 0 || !maxFileSizeBytes || maxFileSizeBytes <= 0) {
      return null;
    }

    return {
      enableTranscription: (parsed.enableTranscription ?? parsed.EnableTranscription) as boolean | undefined,
      maxDurationSeconds,
      maxFileSizeBytes,
    };
  } catch {
    return null;
  }
};

export const parseVoiceValue = (rawValue: string | undefined): ConsultationVoiceValue => {
  if (!rawValue?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    const rawAudio = parsed.audio ?? parsed.Audio;
    const rawTranscription = parsed.transcription ?? parsed.Transcription;

    return {
      ...(rawAudio ? { audio: normalizeAudioItem(rawAudio) ?? undefined } : {}),
      ...(rawTranscription ? { transcription: normalizeTranscriptionItem(rawTranscription) ?? undefined } : {}),
    };
  } catch {
    return {};
  }
};

export const serializeVoiceValue = (value: ConsultationVoiceValue): string => JSON.stringify(value);

export const createEmptyVoiceValue = (): ConsultationVoiceValue => ({});

export const hasConsultationVoiceValue = (rawValue: string | undefined): boolean =>
  Boolean(parseVoiceValue(rawValue).audio?.id);

export const formatVoiceDuration = (durationSeconds?: number): string => {
  if (!durationSeconds || durationSeconds <= 0) {
    return '0:00';
  }

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const validateLocalVoiceFile = (
  schema: ConsultationVoiceSchema,
  file: File,
  durationSeconds?: number,
): string | null => {
  const maxFileSizeBytes = schema.maxFileSizeBytes ?? DEFAULT_VOICE_SCHEMA.maxFileSizeBytes ?? 0;
  if (file.size > maxFileSizeBytes) {
    return `Recording exceeds the configured max size of ${Math.round(maxFileSizeBytes / (1024 * 1024))} MB`;
  }

  const contentType = file.type || 'application/octet-stream';
  if (!contentType.startsWith('audio/') && contentType !== 'application/octet-stream') {
    return 'Recording must be an audio file';
  }

  const maxDurationSeconds = schema.maxDurationSeconds ?? DEFAULT_VOICE_SCHEMA.maxDurationSeconds ?? 0;
  if (durationSeconds && durationSeconds > maxDurationSeconds) {
    return `Recording exceeds the configured max duration of ${maxDurationSeconds} seconds`;
  }

  return null;
};

export const voiceSchemaToEditorState = (schemaJson?: string) => {
  const schema = parseConsultationVoiceSchema(schemaJson) ?? DEFAULT_VOICE_SCHEMA;
  return {
    enableTranscription: schema.enableTranscription ?? true,
    maxDurationSeconds: schema.maxDurationSeconds ?? DEFAULT_VOICE_SCHEMA.maxDurationSeconds ?? 300,
    maxFileSizeMb: Math.max(1, Math.round((schema.maxFileSizeBytes ?? DEFAULT_VOICE_SCHEMA.maxFileSizeBytes ?? 0) / (1024 * 1024))),
  };
};

export const editorStateToVoiceSchemaJson = (state: {
  enableTranscription: boolean;
  maxDurationSeconds: number;
  maxFileSizeMb: number;
}): string => JSON.stringify({
  enableTranscription: state.enableTranscription,
  maxDurationSeconds: Math.max(1, state.maxDurationSeconds),
  maxFileSizeBytes: Math.max(1, state.maxFileSizeMb) * 1024 * 1024,
}, null, 2);
